import { FormEvent, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { cloneDeep } from "lodash";

import Checklist, { ChecklistChangeEvent, ChecklistItem } from "@/Administration/Components/Services/ExportServicePopup/ExportServiceForm/Checklist/Checklist";
import { IServiceComboGridModel } from "@/Administration/Models/UI/ServiceCombo/IServiceComboGridModel";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";

import "./ExportServiceForm.scss";

interface Values {
    sites: string[];
    specialties: string[];
    employees: Guid[];
    services: string[];
    startDate: Date;
    endDate: Date;
}

type FilterItems = Record<keyof Omit<Values, "startDate" | "endDate">, ChecklistItem[]>;

interface ExportServiceProps {
    model: IServiceComboGridModel;
    onChange?: (value: Values) => void;
    onSubmit?: (value: Values) => void;
}

interface ExportServiceFormHandle {
    submit: () => void;
    isValid: () => boolean;
    requestSubmit: () => void;
}

const defaultFilters: Values = {
    sites: [],
    specialties: [],
    employees: [],
    services: [],
    startDate: null,
    endDate: null
};

const ExportServiceForm = forwardRef<ExportServiceFormHandle, ExportServiceProps>((props, ref) => {
    const { model, onChange, onSubmit } = props;

    const formRef = useRef<HTMLFormElement>(null);

    const { getText: t } = useTranslation();
    const [values, setValues] = useState<Values>(defaultFilters);

    const filters = useMemo<FilterItems>(
        () => ({
            sites: model.get("Sites")
                .map(site => ({ value: site.Id, label: site.Model.ClinicalName }))
                .toArray(),
            specialties: model.get("Specializations")
                .filter(({ Code }) => Code != "Ad-HocSpe")
                .map(specialty => ({ value: specialty.Id, label: specialty.Name }))
                .toArray(),
            employees: model.get("EmployeesFiltered")
                .map(employee => ({ value: employee.ContactFacetId, label: employee.Name }))
                .toArray(),
            services: cacheStore.ServiceDefinitionCache
                .filter(service =>
                    service.IsAdHoc == false
                    && (!!values.sites.length ? values.sites.includes(service.GetSiteId()) : true)
                    && (!!values.specialties.length ? values.specialties.includes(service.GetSpecializationId()) : true)
                    && (!!values.employees.length ? values.employees.some(id => service.Employees.map(({ ContactFacetId }) => ContactFacetId).includes(id)) : true)
                )
                .map(service => ({
                    value: service.Model.Id,
                    label: service.Name
                }))
                .toArray()
        }),
        [model, values.employees, values.sites, values.specialties]
    );

    const startDate = useMemo(() => {
        const [date] = values.startDate?.toISOString()?.split("T") ?? [""];

        return date;
    }, [values.startDate]);

    const endDate = useMemo(() => {
        const [date] = values.endDate?.toISOString()?.split("T") ?? [""];

        return date;
    }, [values.endDate]);

    const areDatesValid = useMemo(
        () => values.endDate >= values.startDate,
        [values.startDate, values.endDate]
    );

    const handleChecklistChange = (e: ChecklistChangeEvent): void => {
        const { name, value } = e;

        setValues(currentValues => {
            if (!(name in values)) {
                throw Error("Wrong Filter key provided!.");
            }

            const newValues = cloneDeep(currentValues);

            newValues[name] = value;

            return newValues;
        });
    };

    const handleDateChange = (event: { value: Date | null, name: "startDate" | "endDate" }): void => {
        const { value, name } = event;

        setValues(currentValues => {
            const newValues = cloneDeep(currentValues);

            newValues[name] = value;

            return newValues;
        });
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault();

        if (!areDatesValid || !values.services.length) {
            return;
        }

        onSubmit?.(values);
    };

    useImperativeHandle(
        ref,
        () => ({
            isValid: () => (!!values.endDate && !!values.startDate && (values.endDate >= values.startDate) && !!values.services.length),
            requestSubmit: () => formRef.current.requestSubmit(),
            submit: () => formRef.current.submit()
        }),
        [values.endDate, values.services, values.startDate]
    );

    useEffect(() => {
        onChange?.(values);
    }, [onChange, values]);

    return (
        <>
            <form
                action={apiSettings.buildAppUrl("api/services/exportTimeSlotsCSV.csv", { auth: true })}
                target="_blank"
                method="post"
                ref={formRef}
                hidden
            >
                {values.services.map(id => (
                    <input name="ServiceIds" type="checkbox" value={id} key={id} defaultChecked={true} required />
                ))}

                {values.employees.map(id => (
                    <input name="EmployeeIds" type="checkbox" value={id} key={id} defaultChecked={true} />
                ))}

                <input type="date" name="dateFrom" defaultValue={startDate} required />
                <input type="date" name="dateTo" defaultValue={endDate} required />
            </form>

            <form onSubmit={handleSubmit} className="export-service-form">
                <div className="flex-item">
                    <h5><strong>{t("FilterYourServicesLabel")}</strong></h5>

                    <div className="flex-container flex-gap-sm">
                        <Checklist
                            name="sites"
                            title={t("SiteDLabel")}
                            onChange={handleChecklistChange}
                            items={filters.sites}
                            values={values.sites}
                        />

                        <Checklist
                            name="specialties"
                            title={t("SpecialityLabel")}
                            onChange={handleChecklistChange}
                            items={filters.specialties}
                            values={values.specialties}
                        />

                        <Checklist
                            name="employees"
                            title={t("EmployeeLabel")}
                            onChange={handleChecklistChange}
                            items={filters.employees}
                            values={values.employees}
                        />
                    </div>
                </div>

                <div className="divider" />

                <div className="flex-item">
                    <h5><strong>{t("FilterYourServicesLabel")}</strong></h5>

                    <div className="flex-container flex-gap-sm">
                        <Checklist
                            searchable
                            requiredMark
                            name="services"
                            title={t("ServiceLabel")}
                            onChange={handleChecklistChange}
                            items={filters.services}
                            values={values.services}
                        />

                        <div className="checklist flex-item">
                            <div className="checkbox time-frame-label">
                                <label className="checklist-header checklist-header-required">
                                    {t("DateLabel")}
                                </label>
                            </div>

                            <div className="form-group">
                                <DateSelector
                                    id="startDate"
                                    valid={areDatesValid}
                                    date={values.startDate}
                                    onChangeCallback={value => handleDateChange({ value, name: "startDate" })}
                                    dateFormat="yyyy/MM/dd"
                                    ignoreInvalidCharacters
                                    toDayEnabled={true}
                                    width={150}
                                />
                            </div>

                            <div className="form-group">
                                <DateSelector
                                    id="startDate"
                                    valid={areDatesValid}
                                    date={values.endDate}
                                    onChangeCallback={value => handleDateChange({ value, name: "endDate" })}
                                    dateFormat="yyyy/MM/dd"
                                    ignoreInvalidCharacters
                                    toDayEnabled={true}
                                    width={150}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
});

export { ExportServiceFormHandle, Values };

export default ExportServiceForm;
